import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { useLockedTaskUnlockMutation } from '@common/cache';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { fetchEventSource } from '@microsoft/fetch-event-source';

import { components } from '@types';
import { t } from 'i18next';
import { FormattedToastMessage } from '@components';
import { safeRefreshTokens } from '@config/axios/axios.config.ts';
import { useAuthTokens } from '@store';

type eventType =
  | components['schemas']['TaskUnlockRequestedEvent']
  | components['schemas']['TaskLockedEvent']
  | components['schemas']['TaskUnlockReplyEvent']
  | {
      type: string;
      payload: { requester: { full_name: string }; approved: boolean };
    };

const defaultEvent: eventType = {
  type: '',
  payload: { requester: { full_name: '' }, approved: false },
};

export const useTaskEventListener = (task_id: string) => {
  const { token } = useAuthTokens();
  const [event, setEvent] = useState<eventType>(defaultEvent);
  const unlockLockedTaskMutation = useLockedTaskUnlockMutation(task_id);

  const handleUnlockLockedTask = async () => {
    try {
      await unlockLockedTaskMutation.mutateAsync();

      toast.success(
        <FormattedToastMessage
          title={t('worklist.worklistItem.toastMessages.unlockTaskTitle')}
          message={t('worklist.worklistItem.toastMessages.unlockTaskMessage')}
        />,
      );
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleClearEvent = () => {
    setEvent(defaultEvent);
  };

  useEffect(() => {
    if (!task_id || !token) {
      return;
    }

    const controller = new AbortController();
    let isCancelled = false;
    let retryTimeout: number | undefined;
    let retryDelay = 1000;

    const subscribe = async () => {
      try {
        await fetchEventSource(
          `${import.meta.env.VITE_BASE_URL}/task/${task_id}/sse`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
            async onopen(response) {
              if (response.ok) {
                return;
              }

              if (response.status === 401) {
                controller.abort();
                await safeRefreshTokens();
                throw new Error('unauthorized');
              }

              throw new Error(`Unexpected status ${response.status}`);
            },
            onmessage(ev) {
              if (!ev.data) {
                return;
              }

              try {
                const parsed = JSON.parse(ev.data);
                setEvent(parsed);
              } catch (err) {
                // eslint-disable-next-line no-console
                console.log('Task SSE error', err);
              }
            },
            onerror(err) {
              // eslint-disable-next-line no-console
              console.log('Task SSE error', err);
              throw err;
            },
            openWhenHidden: true,
          },
        );
        retryDelay = 1000;
      } catch (err) {
        if (isCancelled) {
          return;
        }

        const message = (err as Error).message;

        if (message === 'unauthorized') {
          return;
        }

        retryTimeout = window.setTimeout(() => {
          if (!isCancelled) {
            subscribe();
          }
        }, retryDelay);

        retryDelay = Math.min(retryDelay * 2, 30000);
      }
    };

    void subscribe();

    return () => {
      isCancelled = true;
      controller.abort();
      if (retryTimeout) {
        window.clearTimeout(retryTimeout);
      }
    };
  }, [task_id, token]);

  return {
    event,
    handleUnlockLockedTask,
    handleClearEvent,
  };
};
